import { useQuery } from "@tanstack/react-query";
import { apiClient, signApiRequest } from "./";
import {
  isFullCredentials,
  type CustomerCredentials,
} from "../ui/customerDataStore";

// Why a policy is nothing we can improve. Codes, so the copy lives on the UI.
export type NonTransferableReason =
  | "COLLECTIVE_AGREEMENT"
  | "DEFINED_BENEFIT"
  | "ACTIVE_PAYOUT"
  | "NOT_MOVABLE"
  | "NOT_QUALIFIED";

// Every pension collected, transferable or not.
export interface PensionPolicy {
  policyId: string;
  company: string;
  // The employer, for occupational pensions.
  holder: { name: string; organizationNumber: string | null } | null;
  type: string;
  currentValue: number;
  savedAt: string | null;
  transferable: boolean;
  nonTransferableReasons: NonTransferableReason[];
}

export interface SekQualifiedPolicy {
  policyId: string;
  company: string;
  currentFeesSek: number;
  samblaFeesSek: number;
  feeAdvantageSek: number;
}

export interface PercentQualifiedPolicy {
  policyId: string;
  company: string;
  totalFeePercent: number;
  feeAdvantage: number;
}

export interface TeaserData {
  teaserDisplayed: "sekTeaser" | "percentTeaser";
  sekTeaser: string | null;
  percentTeaser: string | null;
  nbSekQualified: number;
  maxFeeAdvantageSek: number;
  minFeeAdvantageSek: number;
  sekQualifiedPolicies: SekQualifiedPolicy[];
  nbPercentQualified: number;
  maxFeeAdvantage: number;
  minFeeAdvantage: number;
  percentQualifiedPolicies: PercentQualifiedPolicy[];
}

export interface InsurelyFeesResponse {
  fid: string;
  growth: number;
  calculatedAt: string;
  policies: PensionPolicy[];
  teaser: TeaserData;
}

// Exported for the delete flow's confirmation read. Pass no leadId there: it re-caches.
export const fetchInsurelyFees = async (
  credentials: CustomerCredentials,
  leadId?: string,
): Promise<InsurelyFeesResponse> => {
  const leadParam = leadId ? `&leadId=${leadId}` : "";
  const url = `${import.meta.env.VITE_API_URL}/calculation/insurely/fees?fid=${credentials.identityId}${leadParam}`;
  const signedRequest = await signApiRequest("GET", url, credentials);
  const response = await apiClient(signedRequest);
  return response.data;
};

export const useGetInsurelyFees = ({
  credentials,
  leadId,
  round = 0,
  enabled = true,
}: {
  credentials: CustomerCredentials;
  leadId?: string;
  /** Collection round. Bumping it recalculates instead of serving the cached round. */
  round?: number;
  enabled?: boolean;
}) =>
  useQuery<InsurelyFeesResponse>({
    queryKey: ["insurelyFees", credentials.identityId, leadId, round],
    queryFn: () => fetchInsurelyFees(credentials, leadId),
    enabled: enabled && isFullCredentials(credentials),
    retry: 3,
    // Per requirements, we expect Insurely iframe to be shown once, and on successful calculation we are ok with stale data
    // as we don't expect any data update per current logic
    staleTime: Infinity,
  });
